/**
 * Guarda Mundo - PDP intro depoimentos ("Quem já tem"): scroll horizontal + dots + autoplay.
 */
import { scrollIntoView } from '@theme/scrolling';

if (!customElements.get('gm-pdp-intro-quote-carousel')) {
  class GmPdpIntroQuoteCarousel extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('[data-gm-quote-viewport]');
      this._slides = [...this.querySelectorAll('[data-gm-quote-slide]')];
      this._dots = [...this.querySelectorAll('[data-gm-quote-dot]')];
      this._interval = parseInt(this.dataset.interval || '6000', 10);
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._timer = null;
      this._paused = false;
      this._current = 0;
      this._navigatingTo = null;

      if (!this._viewport || this._slides.length < 2) return;

      this._onScroll = () => {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => {
          this._syncFromScroll();
          this._resetTimer();
        });
      };

      this._viewport.addEventListener('scroll', this._onScroll, { passive: true });

      this._onScrollEnd = () => {
        this._navigatingTo = null;
        this._syncFromScroll();
      };

      this._viewport.addEventListener('scrollend', this._onScrollEnd, { passive: true });

      this._dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.slideIndex, 10);
          if (!Number.isNaN(index)) {
            this._goTo(index);
            this._resetTimer();
          }
        });
      });

      this.addEventListener('mouseenter', () => {
        this._paused = true;
        this._clearTimer();
      });

      this.addEventListener('mouseleave', () => {
        this._paused = false;
        this._startTimer();
      });

      this.addEventListener('focusin', () => {
        this._paused = true;
        this._clearTimer();
      });

      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this._paused = false;
        this._startTimer();
      });

      this._onVisibility = () => {
        if (document.hidden) {
          this._clearTimer();
        } else if (!this._paused) {
          this._startTimer();
        }
      };

      document.addEventListener('visibilitychange', this._onVisibility);

      this._resizeObserver = new ResizeObserver(() => this._syncFromScroll());
      this._resizeObserver.observe(this._viewport);

      this._syncFromScroll();
      this._startTimer();
    }

    disconnectedCallback() {
      this._viewport?.removeEventListener('scroll', this._onScroll);
      this._viewport?.removeEventListener('scrollend', this._onScrollEnd);
      this._resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._clearTimer();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
      clearTimeout(this._navFallback);
    }

    _getLastIndex() {
      return this._slides.length - 1;
    }

    _getSnapScrollLeft(index) {
      const slide = this._slides[index];
      if (!slide) return 0;

      if (index === this._getLastIndex()) {
        return slide.offsetLeft + slide.offsetWidth - this._viewport.clientWidth;
      }

      return slide.offsetLeft;
    }

    _syncFromScroll() {
      if (this._navigatingTo !== null) {
        const targetScroll = this._getSnapScrollLeft(this._navigatingTo);
        if (Math.abs(this._viewport.scrollLeft - targetScroll) > 2) {
          this._setActive(this._navigatingTo, true);
          return;
        }
        this._navigatingTo = null;
      }

      const lastIndex = this._getLastIndex();
      const scrollLeft = this._viewport.scrollLeft;
      let bestIndex = 0;
      let bestDistance = Infinity;

      for (let index = 0; index <= lastIndex; index += 1) {
        const distance = Math.abs(scrollLeft - this._getSnapScrollLeft(index));
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      }

      this._setActive(bestIndex);
    }

    _setActive(index, force = false) {
      const lastIndex = this._getLastIndex();
      index = Math.max(0, Math.min(index, lastIndex));

      if (!force && index === this._current) return;

      this._current = index;

      this._slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('data-active', isActive);
      });

      this._dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    _goTo(index) {
      const total = this._slides.length;
      const next = ((index % total) + total) % total;
      const slide = this._slides[next];
      if (!slide) return;

      const lastIndex = this._getLastIndex();

      this._navigatingTo = next;
      this._setActive(next, true);

      scrollIntoView(slide, {
        ancestor: this._viewport,
        inline: next === lastIndex ? 'end' : 'start',
        block: 'nearest',
        behavior: this._reduced ? 'auto' : 'smooth',
      });

      if (this._reduced) {
        this._navigatingTo = null;
        return;
      }

      clearTimeout(this._navFallback);
      if (typeof this._viewport.onscrollend === 'undefined') {
        this._navFallback = setTimeout(() => {
          this._navigatingTo = null;
          this._syncFromScroll();
        }, 450);
      }
    }

    _clearTimer() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    _startTimer() {
      this._clearTimer();
      if (this._reduced || this._paused || this._slides.length < 2) return;

      this._timer = setInterval(() => {
        this._goTo(this._current + 1);
      }, this._interval);
    }

    _resetTimer() {
      this._clearTimer();
      if (!this._paused) this._startTimer();
    }
  }

  customElements.define('gm-pdp-intro-quote-carousel', GmPdpIntroQuoteCarousel);
}
